import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import styles from './InvoiceViewPaymentInstallmentsHeader.module.scss';
import {
	InstallmentStatus,
	InvoiceViewPaymentInstallmentHeaderInvoiceFragment,
} from '#types';
import { gql, DocumentNode } from '@apollo/client';
import { useDeviceDetection } from '../../../../hooks/useDeviceDetection';
import rpcShared from '@rockpapercoin/rpc-shared';

const invoiceViewPaymentInstallmentHeaderInvoiceFragment = gql`
	fragment invoiceViewPaymentInstallmentHeaderInvoice on Invoice {
		id
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			...getViewingUserDocumentRelationshipOrganization
		}
		paymentInstallments {
			id
			status
			scheduledPaymentDate
		}
	}
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
	${ rpcShared.organizations
		.getViewingUserDocumentRelationshipOrganizationFragment }
`;

export type InvoiceViewPaymentInstallmentsHeaderProps = {
	invoice?: InvoiceViewPaymentInstallmentHeaderInvoiceFragment;
	requestingUserBaseId?: string;
	warning?: boolean;
	amountLabel: string;
};

type InvoiceViewPaymentInstallmentsHeaderPropsWithFragment =
	React.FC<InvoiceViewPaymentInstallmentsHeaderProps> & {
		fragments: {
			invoiceViewPaymentInstallmentHeaderInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentInstallmentsHeader: InvoiceViewPaymentInstallmentsHeaderPropsWithFragment =
	( { invoice, requestingUserBaseId, warning, amountLabel } ) => {
		const { isMobile } = useDeviceDetection();

		const {
			viewingUserIsCustomer,
			viewingUserMayAct: viewingUserIsAPPAWithPaymentPerms,
		} = rpcShared.contacts.getViewingUserDocumentRelationship( {
			contact: invoice?.contact,
			viewingUserId: requestingUserBaseId,
			permission: 'plannerOrgCanPayInvoices',
		} );
		const { viewingUserIsMember: viewingUserIsVendorMember } =
			rpcShared.organizations.getViewingUserDocumentRelationship( {
				organization: invoice?.organization,
				viewingUserId: requestingUserBaseId,
			} );
		const viewingUserIsCustomerOrAPPAWithPaymentPerms =
			viewingUserIsCustomer || viewingUserIsAPPAWithPaymentPerms;

		const hasPayableInstallments = invoice?.paymentInstallments?.some(
			( installment ) =>
				installment.status === InstallmentStatus.Unpaid &&
				!installment.scheduledPaymentDate
		);

		const getHeaderClassNames = () => {
			if ( !viewingUserIsVendorMember && hasPayableInstallments ) {
				return styles.desktopHeaderForCustomer;
			} else {
				return styles.desktopHeaderForVendor;
			}
		};

		return (
			<>
				<Box className={ styles.container }>
					<Typography
						variant='h6'
						data-cy='installments-header'
						className={ styles.header }
					>
						Payment Schedule
					</Typography>
					{ viewingUserIsCustomerOrAPPAWithPaymentPerms &&
					hasPayableInstallments ? (
							<Box>
								<Typography
									data-cy='payable-installments-pretext'
									className={ [ styles.pretext, warning ? styles.warning : undefined, ].join( ' ' ) }
								>
								Select installments you would like to pay
								</Typography>
							</Box>
						) : null }
					{ isMobile ? (
						<>
							<Box
								className={ styles.mobileHeader }
								data-cy='mobile-installments-text'
							>
								<Typography>Installments</Typography>
							</Box>
						</>
					) : (
						<Box className={ getHeaderClassNames() }>
							{ viewingUserIsCustomerOrAPPAWithPaymentPerms &&
							hasPayableInstallments ? (
									<Box className={ styles.column }></Box>
								) : null }
							<Typography component='div' data-cy='header-name'>
								Name
							</Typography>
							<Typography component='div' data-cy='header-amount'>
								{ amountLabel }
							</Typography>
							<Typography component='div' data-cy='header-dueDate'>
								Due date
							</Typography>
							<Typography component='div' data-cy='header-datePaid'>
								Date paid
							</Typography>
							<Typography component='div' data-cy='header-status'>
								Status
							</Typography>
							{ viewingUserIsVendorMember ? (
								<Box className={ styles.column }></Box>
							) : null }
						</Box>
					) }
				</Box>
				<Divider />
			</>
		);
	};

InvoiceViewPaymentInstallmentsHeader.fragments = {
	invoiceViewPaymentInstallmentHeaderInvoiceFragment,
};
